import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { sortBy, cloneDeep } from "lodash-es";
import { Section } from "shared/layout";
import Button, { ButtonSize, ButtonPurpose } from "shared/Button";
import Card from "shared/Card";
import styles from "./SSFSort.module.css";
import { useSSFSortData } from "./useSSFSortData";
import { useCallback, useLayoutEffect, useRef } from "react";
import { Hamburger } from "react-ikonate";
import createNotification from "shared/@utils/createNotification";

/**
 * Structure component. Provides a way to order files in top-level SSF categories.
 *
 * This is being created as a beyond-last-minute way for Marketing to control an aspect
 * of content that was requested by the Diamonds but was left out of the scope for development.
 * It does not adhere to the usual quality standards for stories, testing or how to break down components.
 */
export function SSFSort() {
  const data = useSSFSortData();
  const contentRef = useRef(null);
  const previousPositions = useRef(null);

  const cancelChanges = () => {
    const items = contentRef.current?.querySelectorAll(
      "[data-rbd-draggable-id]"
    );
    previousPositions.current = items
      ? new Map(
          Array.from(items, (item) => [
            item.getAttribute("data-rbd-draggable-id"),
            item.getBoundingClientRect(),
          ])
        )
      : null;
    data.reset();
  };

  useLayoutEffect(() => {
    const previous = previousPositions.current;
    if (!previous) return;
    previousPositions.current = null;

    if (
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ||
      typeof Element.prototype.animate !== "function"
    ) {
      return;
    }

    contentRef.current
      ?.querySelectorAll("[data-rbd-draggable-id]")
      .forEach((item) => {
        const oldPosition = previous.get(
          item.getAttribute("data-rbd-draggable-id")
        );
        if (!oldPosition) return;

        const newPosition = item.getBoundingClientRect();
        const offsetX = oldPosition.left - newPosition.left;
        const offsetY = oldPosition.top - newPosition.top;
        if (offsetX === 0 && offsetY === 0) return;

        item.animate(
          [
            { transform: `translate(${offsetX}px, ${offsetY}px)` },
            { transform: "translate(0, 0)" },
          ],
          {
            duration: 250,
            easing: "cubic-bezier(0.2, 0, 0, 1)",
          }
        );
      });
  }, [data.categories]);

  const onDragEnd = useCallback(
    (result) => {
      if (
        !result?.source ||
        !result.destination ||
        result.source.droppableId !== result.destination.droppableId ||
        !Number.isInteger(result.source.index) ||
        !Number.isInteger(result.destination.index)
      ) {
        return;
      }

      const categoryId = Number(
        result.destination.droppableId.replace(/^category-/, "")
      );
      if (!Number.isFinite(categoryId)) return;

      const category = data.categories.find((c) => c.id === categoryId);
      if (!category || !Array.isArray(category.files)) return;

      const files = cloneDeep(sortBy(category.files, "sortOrder"));
      if (
        result.source.index < 0 ||
        result.source.index >= files.length ||
        result.destination.index < 0 ||
        result.destination.index >= files.length ||
        result.source.index === result.destination.index
      ) {
        return;
      }

      const source = files.splice(result.source.index, 1);
      files.splice(result.destination.index, 0, ...source);

      let sortOrder = 1;

      for (const file of files) {
        file.sortOrder = sortOrder++;
      }

      data.setCategories(
        data.categories.map((cat) => {
          if (cat.id === categoryId) {
            return {
              ...cat,
              files,
            };
          } else {
            return cat;
          }
        })
      );
    },
    [data]
  );

  return (
    <Card>
      <Section>
        <header>
          <h3>SSF Content Sort Order</h3>

          <div className={styles.headerControls}>
            <Button
              size={ButtonSize.small}
              purpose={ButtonPurpose.default}
              disabled={!data.isChanged || data.isLoading}
              onClick={cancelChanges}
            >
              Cancel
            </Button>
            <Button
              size={ButtonSize.small}
              purpose={ButtonPurpose.save}
              disabled={!data.isChanged || data.isLoading}
              onClick={() => {
                data.saveMutation.mutateAsync().then(() => {
                  createNotification({
                    type: "success",
                    title: "Saved",
                    message: "Sort order saved",
                    autoDismiss: true,
                  });
                });
              }}
            >
              Save
            </Button>
          </div>
        </header>

        <DragDropContext onDragEnd={onDragEnd}>
          {data.isSuccess ? (
            <div className={styles.content} ref={contentRef}>
              {data.categories.map((category) => {
                return (
                  <Droppable
                    key={category.id}
                    droppableId={`category-${category.id}`}
                    type={`category-${category.id}`}
                  >
                    {(provided) => (
                      <section
                        className={styles.category}
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                      >
                        <header>
                          <h4>
                            Step {category.stepNumber}: {category.title}
                          </h4>
                        </header>
                        <ul>
                          {sortBy(category.files, "sortOrder").map(
                            (file, index) => {
                              return (
                                <Draggable
                                  key={file.id}
                                  draggableId={`file-${file.id}`}
                                  index={index}
                                >
                                  {(provided) => (
                                    <li
                                      ref={provided.innerRef}
                                      {...provided.draggableProps}
                                      {...provided.dragHandleProps}
                                    >
                                      <Hamburger className={styles.dragIcon} />
                                      <span className={styles.fileName}>
                                        {file.name}
                                      </span>
                                      {file.subtitle && (
                                        <>
                                          <span>&ndash;</span>
                                          <span className={styles.fileSubtitle}>
                                            {file.subtitle}
                                          </span>
                                        </>
                                      )}
                                    </li>
                                  )}
                                </Draggable>
                              );
                            }
                          )}
                          {provided.placeholder}
                        </ul>
                      </section>
                    )}
                  </Droppable>
                );
              })}
            </div>
          ) : (
            "Loading..."
          )}
        </DragDropContext>
      </Section>
    </Card>
  );
}
